import { z } from "zod";
import { CustomerResponseEntrySchema } from "../services/customersService";
import { GetProductsResponseEntrySchema } from "../services/productsService";
import { agreementsApiSchema } from "./agreements";

export const DOC_SEGMENTS = {
    CUSTOMER: "CUST",
    INVESTMENT: "INVE",
    WBS: "WBS",
    PRODUCT: "PROD",
    STYLE: "STYL",
    OFFER: "OFFE",
    AGREEMENT: "AGRE",
    CHANNEL: "CHAN",
    TRANSFER: "TRAN"
};

// Corresponds to POST /api/files/home/upload-token/
export const FileUploadTokenRequestSchema = z.object({
    file_ori_name: z.string(),
    content_type: z.string(),
    unique_index: z.string(),
});
export const MultipleFileUploadTokenRequestSchema = z.array(FileUploadTokenRequestSchema);

export const FileUploadTokenResponseSchema = z.object({
    file_ori_name: z.string(),
    unique_index: z.string(),
    file_name: z.string(),
    upload_url: z.string().url(),
    miniature_file_name: z.string().optional(),
    miniature_upload_url: z.string().url().optional(),
});
export const MultipleFileUploadTokenResponseSchema = z.array(FileUploadTokenResponseSchema);

// Corresponds to POST /api/files/home/mass-action/
export const DocumentCreateSchema = z.object({
    content_type: z.string(),
    file_ori_name: z.string(),
    file_name: z.string(),
    docs_name: z.string(),
    docs_nr: z.string(),
    revision_parent: z.string().uuid().optional(),
    industry: z.array(z.string().uuid()).optional(),
    docs_restricted_role: z.array(z.string().uuid()).optional(),
    docs_restricted_user: z.array(z.string()).optional(),
});

export const MassActionRequestSchema = z.object({
    docs: z.array(DocumentCreateSchema),
});

// Corresponds to POST /api/files/home/docs/
export const DocumentDetailsRequestSchema = z.object({
    docs: z.array(z.string().uuid()),
});

// Corresponds to response from GET /api/files/home/docs/
export const DocumentDetailsSchema = z.object({
    id: z.string().uuid(),
    docs_name: z.string(),
    docs_nr: z.string(),
    docs_parent_nr: z.string().nullable(),
    docs_revision_nr: z.number().nullable(),
    docs_content_type: z.string(),
    docs_industry: z.string(), // This is a string of names, not IDs
    creator: z.string(),
    updated: z.string().datetime(),
    created: z.string().datetime(),
    miniature_download_link: z.string().url().nullable(),
    note: z.string().optional().nullable(),
    is_card_jpg: z.boolean().optional().nullable(), // For product cards
    is_card_pdf: z.boolean().optional().nullable(), // For product cards
    docs_restricted_user: z.string(), // This is a string of names
    docs_restricted_role: z.string(), // This is a string of names
    related_products: z.array(z.string().uuid()).optional(), // This is added client-side
    related_invests: z.array(z.string().uuid()).optional(), // This is added client-side
    investment_name: z.string().optional().nullable(), // For investment docs
    is_prospectus: z.boolean().optional().nullable(), // For investment docs
});

export const PartialDocumentDetailsSchema = z.object({
    id: z.string().uuid(),
    note: z.string().optional().nullable(),
});

export const DocumentDetailsResponseSchema = z.union([
    DocumentDetailsSchema,
    PartialDocumentDetailsSchema,
    z.string().uuid(),
]);

export const DocumentDetailsArrayResponseSchema = z.array(DocumentDetailsResponseSchema);

export const isFullDocumentDetails = (doc) => {
    return doc && typeof doc === "object" && "docs_name" in doc && "docs_nr" in doc;
};

// Corresponds to PATCH /api/files/home/docs/<uuid:doc_id>/update/
export const DocumentUpdateSchema = z.object({
    docs_restricted_user: z.array(z.string().uuid()).optional(),
    docs_restricted_role: z.array(z.string().uuid()).optional(),
    industry: z.array(z.string().uuid()).optional(),
});

// Corresponds to GET /api/files/home/document/<uuid:document_id>/download/
export const DocumentDownloadResponseSchema = z.object({
    download_link: z.string().url(),
    content_type: z.string(),
    docs_name: z.string().optional().nullable(),
});

// Schemas for Sales backend connections (not Home proxy)
export const ConnectDocumentToCustomerRequestSchema = z.object({
    docscust_customer: z.string().uuid(),
    docscust_docs: z.string().uuid(),
    note: z.string().optional(),
});

export const ConnectDocumentToProductRequestSchema = z.object({
    docsprod_product: z.string().uuid(),
    docsprod_docs: z.string().uuid(),
});

export const ConnectDocumentToAgreementRequestSchema = z.object({
    docsagre_agreement: z.string().uuid(),
    docsagre_docs: z.string().uuid(),
    note: z.string().optional(),
});

export const CustomerDocumentSchema = z.object({
    docscust_customer: CustomerResponseEntrySchema,
    docscust_docs: z.string().uuid(),
});

export const ProductDocumentSchema = z.object({
    docsprod_product: GetProductsResponseEntrySchema,
    docsprod_docs: z.string().uuid(),
});

export const AgreementDocumentSchema = z.object({
    docsagre_agreement: agreementsApiSchema.agreement,
    docsagre_docs: z.string().uuid(),
});

export const DeleteDocumentFromCustomerRequestSchema = z.object({
    document_id: z.string().uuid(),
    customer_id: z.string().uuid(),
});

export const DeleteDocumentFromProductRequestSchema = z.object({
    document_id: z.string().uuid(),
    product_id: z.string().uuid(),
});

export const DeleteDocumentFromAgreementRequestSchema = z.object({
    document_id: z.string().uuid(),
    agreement_id: z.string().uuid(),
});

export const UpdateDocumentProductsRequestSchema = z.object({
    document_id: z.string().uuid(),
    product_ids: z.array(z.string().uuid()),
    note: z.string().optional(),
    is_card_jpg: z.boolean().optional(),
    is_card_pdf: z.boolean().optional(),
});

export const DocsFilteringDataSchema = z.object({
    investments: z.array(z.object({ id: z.string().uuid(), investment_name_sales: z.string() })),
    industries: z.array(z.object({ id: z.string().uuid(), industry_name: z.string() })),
    standards: z.array(z.object({ id: z.string().uuid(), docs_investan_name: z.string() })),
    docs_restricted_user: z.array(z.object({ id: z.string().uuid(), full_name: z.string() })),
    docs_restricted_role: z.array(z.object({ id: z.string().uuid(), role_name: z.string() })),
    docs_restricted_type: z.array(z.string()),
    sales_default_roles	: z.array(z.string()),
});

// For compatibility
export const DocumentSchema = DocumentCreateSchema;

export const GenerateDocumentNumberSchema = z.object({
    segment: z.enum(Object.values(DOC_SEGMENTS)),
});

export const GenerateDocumentNumberResponseSchema = z.object({
    segment: z.string(),
    document_number: z.string(),
});

// Utilities
const IMG_CONTENT_TYPES_SUPPORTED_PREVIEW = ["image/jpeg", "image/png"];
const CONTENT_TYPES_SUPPORTED_PREVIEW = [...IMG_CONTENT_TYPES_SUPPORTED_PREVIEW, "application/pdf"];

export const isThumbnailAvailable = (document) => {
    return (
        document.miniature_download_link != null ||
        IMG_CONTENT_TYPES_SUPPORTED_PREVIEW.includes(document.docs_content_type)
    );
};

export const isPreviewAvailable = (document) => {
    return CONTENT_TYPES_SUPPORTED_PREVIEW.includes(document.docs_content_type);
};
